// library functions
import { Linear } from "gsap/EasePack";
import { TimelineLite } from "gsap/TimelineLite";
import { TweenLite } from "gsap/TweenLite";
import { TweenMax } from "gsap/TweenMaxBase";

import { GameConfig, LocationID } from "Data/GameConfig";

// pubsub topics
import * as Topics from "Data/Topics";
import { FORCE_FOCUS, REGISTER_FOCUSABLES } from "html-living-framework";

// gameobjects
import { InteriorItemIconReturnItems } from "GameObjects/GameInterior/ReturnItems/InteriorItemIconReturnItems";

import type { ItemID } from "Data/GameConfig";
import type { IItemConfig } from "Interfaces/IItemConfig";
import type { IItemState } from "Interfaces/IItemState";
import type { ILevelState } from "Interfaces/ILevelState";

export class InteriorItemsListReturnItems extends PIXI.Container {
// children
    public icons: InteriorItemIconReturnItems[] = [];

// gameobject lifecycle
    protected _size?: PIXI.PointLike;
    protected _subscriptions: any[] = [];
    private animateInTimeline?: gsap.Animation;

// state
    private _state!: ILevelState;

    // tslint:disable-next-line: max-line-length
    private _animateOutMode: "fadeAndRotate" | "fade" | "slideRight" | "juggle" | "none" | "slideRightSlow" | "danceParty" = "none";

    public constructor() {
        super();
        this._subscriptions.push(
            PubSub.subscribe(Topics.ITEM_RETURNED, this.onMessageReceived.bind(this)),
            PubSub.subscribe(Topics.ALL_ITEMS_RETURNED, this.onMessageReceived.bind(this)),
            PubSub.subscribe(Topics.LEVEL_COMPLETE, this.onMessageReceived.bind(this)),
        );
    }

    public init(pSize: PIXI.Point, pData: ILevelState) {
        this._state = pData;
        this._size = pSize;

        const {items: configItems, ...config} = GameConfig.locations[pData.id];
        const {items: stateItems, ...state} = pData;
        // lookup and merge state.items with config.items
        const items = (Object.keys(stateItems) as ItemID[]).map((id) => {
            return {...stateItems[id]!, ...configItems[id]!, id};
        });

        const levelConfig = config.narratives[state.narrative].variations[state.narrativeVariation];
        const itemslistConfig = {...config.interior.itemslist.return, ...levelConfig.itemslist?.return };

        this._animateOutMode = itemslistConfig.animateOut;

        this.spawnItems(items, pData, itemslistConfig);

        return this;
    }

    public animateIn(pData: {
        getHatPosition: () => PIXI.Point, // in world space
        onDoneWithHat: () => void;
    }) {
        this.animateInTimeline = new TimelineLite()
            // nested timelines so we don't grab the position until we need it
            .call(() => {
                const localHatPosition = this.toLocal(pData.getHatPosition());
                // console.log("start animating items out of hat", localHatPosition);
                this.animateInTimeline = new TimelineLite()
                    .staggerFrom(this.icons, 0.75, {pixi: {
                        positionX: localHatPosition.x,
                        positionY: localHatPosition.y,
                    }}, 0.5)
                    .staggerFrom(this.icons, 0.75, {pixi: {
                        scale: 0,
                    }}, 0.5, 0)
                    .call(pData.onDoneWithHat)
                    .call(() => {
                        PubSub.publishSync(REGISTER_FOCUSABLES, this.icons);
                    })
                ;
                this.icons.forEach((i) => i.visible = true);
            })
            .delay(2.666)
        ;
    }

    public onResize(pSize: PIXI.Point) {
        this._size = pSize;
    }

    public destroy(pOptions?: PIXI.DestroyOptions | boolean) {
        TweenMax.killTweensOf(this);
        this._subscriptions.forEach(PubSub.unsubscribe);
        this.animateInTimeline?.kill();
        super.destroy(pOptions);
    }

    protected onMessageReceived(pTopic: string, pData: any) {
        switch (pTopic) {
            case Topics.ALL_ITEMS_RETURNED:
                this.OnAllItemsReturned(pData as ILevelState);
                break;
            case Topics.ITEM_RETURNED:
                this.OnItemReturned(pData as IItemState);
                break;
        }
    }

    private OnItemReturned(state: IItemState) {
        this._state.items[state.id] = state;
        const icon = this.icons.find((i) => i.interactive && !i.returned);
        PubSub.publishSync(FORCE_FOCUS, icon);
    }

    private OnAllItemsReturned(state: ILevelState) {
        this._state = state;

        switch (this._animateOutMode) {
            case "slideRight":
                TweenLite.to(this, 2.5, {pixi: {positionX: this._size?.x}});
                break;
            case "juggle":
                TweenMax.to(this, 3.5, {
                    pixi: {
                        rotation: 360,
                    },
                    ease: Linear.easeNone,
                }).repeat(-1);
                break;
            case "fadeAndRotate":
                TweenLite.to(this, 1.5, {pixi: {scale: 0, alpha: 0, rotation: -15 }});
                break;
            case "fade":
                TweenLite.to(this, 0.3, {pixi: {alpha: 0}});
                break;
            case "none":
            default:
                break;
        }
    }

    private spawnItems(
        items: Array<IItemState & IItemConfig>,
        pData: ILevelState,
        itemslist: {
            floatingItemsY: number,
            highlightFloatingItems?: boolean,
            ICON_SIZE: number,
            ICON_SPACING: number,
        },
    ) {
        for (let i = 0; i < items.length; i++) {
            const x = (i - (items.length / 2) + 0.5) * (itemslist.ICON_SIZE + itemslist.ICON_SPACING);
            const y = itemslist.floatingItemsY;
            const newIcon = this.spawnIcon(items[i], pData.id, itemslist);
            newIcon.position.set(x, y);
        }
    }

    private spawnIcon(
        item: IItemState & IItemConfig,
        location: LocationID,
        itemslist: {
            highlightFloatingItems?: boolean,
            floatingItemsY: number,
            returnItemAnchorY?: number,
            ICON_SIZE: number,
            ICON_SPACING: number,
        },
    ): InteriorItemIconReturnItems {
        const newIcon = new InteriorItemIconReturnItems(item, location, itemslist);
        newIcon.visible = false;
        this.icons.push(this.addChild(newIcon));
        return newIcon;
    }

}
